// src/scenes/licenses/components/LicenseRegister.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { createLicense } from "../../../services/licenseService";
import { getVehiclesForCurrentUser } from "../../../services/vehicleService";

/* ===== Paletas coherentes con VehiclesRegister ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const LicenseRegister = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const navigate = useNavigate();

  const { userId, driverId, type: userType } = useSelector((state) => state.user);

  // Form state
  const [licenseNumber, setLicenseNumber] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [vehicles, setVehicles] = useState([]);

  // UX state
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const inputSx = {
    flexGrow: 1,
    "& .MuiInputLabel-root": { color: palette.label },
    "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
    "& .MuiInputBase-input": { color: palette.text },
    "& .MuiInputBase-input.Mui-focused": { color: palette.text },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.cardBorder,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.label,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.accent,
        borderWidth: 2,
      },
    },
  };

  useEffect(() => {
    const loadVehicles = async () => {
      if (!userId && !driverId) return;
      try {
        const data = await getVehiclesForCurrentUser({ userId, driverId, userType });
        setVehicles(data || []);
        if (!vehicleId && data?.length === 1) {
          setVehicleId(data[0]._id);
        }
      } catch (err) {
        console.error("Error al cargar vehículos:", err);
        setVehicles([]);
      }
    };

    loadVehicles();
  }, [driverId, userId, userType, vehicleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!licenseNumber.trim()) return setToast({ type: "error", msg: "El número de licencia es obligatorio." });
    if (!userId) return setToast({ type: "error", msg: "No se ha encontrado el usuario en sesión." });

    const newLicense = {
      licenseNumber: licenseNumber.trim(),
      vehicle: vehicleId || null,
      user: userId,
    };

    try {
      setLoading(true);
      await createLicense(newLicense);
      setToast({ type: "success", msg: "Licencia creada correctamente." });
      navigate("/licenses");
    } catch (error) {
      setToast({ type: "error", msg: error?.message || "Error al crear licencia." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header arriba */}
      <Box sx={{ m: "20px" }}>
        <Header title="Licencias" subtitle="Registrar una nueva licencia" />
      </Box>

      {/* Contenedor de la card */}
      <Box
        sx={{
          minHeight: "calc(100vh - 220px)",
          display: "grid",
          placeItems: "start center",
          px: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: "14px",
            p: 3,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 8px 22px rgba(2,6,23,0.08)"
                : "0 3px 10px rgba(0,0,0,0.24)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: "900px",
            mx: "auto",
          }}
        >
          {/* Cabecera de la card */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={1.25}
          >
            <Box>
              <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
                Registro de licencia
              </Typography>
              <Typography variant="body2" sx={{ color: palette.label }}>
                Añade la licencia y, si quieres, vincúlala a un vehículo existente.
              </Typography>
            </Box>

            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: palette.cardBorder,
                  color: palette.text,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: palette.label },
                }}
              >
                Volver
              </Button>
            </Stack>
          </Stack>

          {/* Formulario */}
          <Box
            component="form"
            id="license-form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: "transparent",
              border: `1px dashed ${palette.cardBorder}`,
              borderRadius: "12px",
              p: 3,
              width: "100%",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Número de licencia"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  required
                  helperText="Ej. ABC-123 o el identificador oficial"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
                <TextField
                  select
                  fullWidth
                  label="Vehículo (opcional)"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  helperText={
                    vehicles.length
                      ? "Asocia un vehículo a la licencia (opcional)."
                      : "No hay vehículos disponibles todavía."
                  }
                  disabled={vehicles.length === 0}
                  sx={inputSx}
                >
                  {vehicles.map((v) => (
                    <MenuItem key={v._id} value={v._id}>
                      {[v.brand, v.model].filter(Boolean).join(" ") || "Vehículo"}
                      {v.licensePlate ? ` (${v.licensePlate})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Botonera inferior */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1.25}
              justifyContent="flex-end"
              alignItems="center"
              mt={3}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/licenses")}
                disabled={loading}
                sx={{
                  borderColor: palette.cardBorder,
                  color: palette.text,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: palette.label },
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: palette.btn,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { backgroundColor: palette.btnHover },
                }}
              >
                {loading ? "Guardando..." : "Guardar licencia"}
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Toast */}
        <Snackbar
          open={!!toast}
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {toast ? (
            <Alert onClose={() => setToast(null)} severity={toast.type} variant="filled" sx={{ width: "100%" }}>
              {toast.msg}
            </Alert>
          ) : null}
        </Snackbar>
      </Box>
    </>
  );
};

export default LicenseRegister;
